import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import CampaignCustomizerModal from './CampaignCustomizerModal';
import ContactPickerModal from './ContactPickerModal';
import CandidateCampaignsSection from './CandidateCampaignsSection';

/**
 * AddToCampaignSidebar — shared multi-step enrollment flow.
 *
 * Used by BOTH:
 *   - CandidatePreviewPanel (single candidate, docked in the left panel
 *     column alongside Presentation/Feedback/Opportunities)
 *   - SearchDetailPage (multi-candidate bulk enrollment from the pool's
 *     bulk action bar, rendered as a fixed right-side overlay)
 *
 * Contract:
 *   - Content-only. Renders `flex-1 flex flex-col overflow-hidden` and
 *     fills its container. Parent controls positioning + width.
 *   - candidateIds is always an array. Length === 1 is the single-candidate
 *     flow; length > 1 is bulk and SKIPS the customize step (per-candidate
 *     subject/body customization isn't meaningful when applied to many).
 *   - Loads campaigns on mount. Caches in local state — a fresh mount = a
 *     fresh fetch, which matches the previous modal-based behavior.
 *   - onEnrolled receives the raw enroll response ({ enrolled, skipped, ... }).
 *     Parent is responsible for showing feedback + closing.
 *
 * Steps:
 *   'pick'      → campaign list with search + smart sort
 *   'customize' → CampaignCustomizerModal inline (single-candidate only)
 *   'contacts'  → ContactPickerModal inline (email/phone selection)
 */
const AddToCampaignSidebar = ({ candidateIds, pipelineId, onClose, onEnrolled }) => {
  const isBulk = candidateIds.length > 1;
  const canCustomize = !isBulk;

  const [step, setStep] = useState('pick');
  const [pickedCampaign, setPickedCampaign] = useState(null);
  const [campaignSearch, setCampaignSearch] = useState('');
  const [campaignList, setCampaignList] = useState([]);
  const [loading, setLoading] = useState(false);
  // For single-candidate flows: overrides map keyed by candidate id, keyed
  // by step position — passed through ContactPickerModal to the enroll
  // request. Bulk skips this entirely.
  const [stepOverrides, setStepOverrides] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/outreach/campaigns`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        });
        if (res.ok) {
          const all = await res.json();
          setCampaignList(all.filter(c => c.status !== 'archived'));
        }
      } catch (e) {
        console.error('[banyak-outreach-sidebar] fetch campaigns failed', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCampaignPick = (campaignId) => {
    const camp = campaignList.find(c => c.id === campaignId);
    setPickedCampaign({ id: campaignId, name: camp?.name || 'Campaign' });
    setStep(canCustomize ? 'customize' : 'contacts');
  };

  const handleCustomizerBack = () => {
    setPickedCampaign(null);
    setStep('pick');
  };

  const handleCustomizerContinue = (overrides) => {
    if (canCustomize && Object.keys(overrides || {}).length > 0) {
      setStepOverrides({ [candidateIds[0]]: overrides });
    }
    setStep('contacts');
  };

  const handleContactPickerBack = () => {
    setStepOverrides(null);
    setStep(canCustomize ? 'customize' : 'pick');
  };

  // Smart sort — priority (high → low):
  //   1. Currently-active campaigns (status = 'active')
  //   2. Campaigns with in-flight sequences (active_sequences > 0)
  //   3. Recency of the campaign's last update (updated_at desc)
  const statusPriority = (s) => (s === 'active' ? 0 : s === 'paused' ? 1 : s === 'draft' ? 2 : 3);
  const sortedCampaigns = [...campaignList].sort((a, b) => {
    const sp = statusPriority(a.status) - statusPriority(b.status);
    if (sp !== 0) return sp;
    const ac = (b.active_sequences || 0) - (a.active_sequences || 0);
    if (ac !== 0) return ac;
    const at = new Date(a.updated_at || a.created_at || 0).getTime();
    const bt = new Date(b.updated_at || b.created_at || 0).getTime();
    return bt - at;
  });
  const q = campaignSearch.trim().toLowerCase();
  const visibleCampaigns = q
    ? sortedCampaigns.filter(c => (c.name || '').toLowerCase().includes(q))
    : sortedCampaigns;

  const headerLabel =
    step === 'customize' ? 'Customize campaign' :
    step === 'contacts'  ? (isBulk ? `Confirm contacts · ${candidateIds.length} candidates` : 'Confirm contacts') :
                          (isBulk ? `Add to Campaign · ${candidateIds.length} candidates` : 'Add to Campaign');

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white">
      <div className="flex-shrink-0 bg-gray-100 border-b border-gray-200 px-3 h-9 flex items-center justify-between overflow-hidden">
        <div className="flex items-center gap-1.5 min-w-0">
          {step === 'customize' && (
            <button onClick={handleCustomizerBack} title="Back to campaign list"
              className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 flex-shrink-0">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {step === 'contacts' && (
            <button onClick={handleContactPickerBack} title={canCustomize ? "Back to customize" : "Back to campaign list"}
              className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 flex-shrink-0">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <svg className="w-4 h-4 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>
          <span className="text-xs font-semibold text-gray-800 flex-shrink-0">Outreach</span>
          {(step === 'customize' || step === 'contacts') && <span className="text-[11px] text-gray-400 truncate">· {headerLabel}</span>}
        </div>
        <button onClick={onClose} title="Close" className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {step === 'pick' && (
        <div className="flex-1 overflow-y-auto px-3 pt-3 pb-3 space-y-3">
          {/* Candidate's current campaign enrollments (single-candidate view only) */}
          {!isBulk && candidateIds?.length === 1 && (
            <CandidateCampaignsSection
              candidateId={candidateIds[0]}
              onManageCampaign={(cid) => window.open(`/outreach/${cid}`, '_blank')}
            />
          )}

          {/* Add to campaign */}
          <div className="space-y-2 pt-1 border-t border-gray-100">
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block">Add to campaign</span>

            <div className="relative">
              <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={campaignSearch}
                onChange={(e) => setCampaignSearch(e.target.value)}
                placeholder="Search campaigns…"
                className="w-full h-8 pl-7 pr-7 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                autoFocus
              />
              {campaignSearch && (
                <button onClick={() => setCampaignSearch('')} title="Clear"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-700 rounded">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
              </div>
            ) : campaignList.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-xs text-gray-500">No campaigns available</p>
                <p className="text-[10px] text-gray-400 mt-1">Create a campaign from the Outreach page first.</p>
              </div>
            ) : visibleCampaigns.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-xs text-gray-500">No matches for "{campaignSearch}"</p>
              </div>
            ) : (
              <div className="space-y-2">
                {visibleCampaigns.map(c => (
                  <div key={c.id} className="border rounded-lg p-2.5 hover:bg-gray-50 flex flex-col gap-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-gray-900 truncate flex-1">{c.name}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${c.status === 'active' ? 'bg-green-100 text-green-700' : c.status === 'paused' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{c.status}</span>
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {c.step_count || 0} step{c.step_count === 1 ? '' : 's'} · {c.total_enrolled || 0} enrolled
                      {c.active_sequences > 0 && (
                        <span className="text-green-600 ml-1">· {c.active_sequences} active</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleCampaignPick(c.id)}
                      className="w-full mt-1 px-2 py-1 text-[11px] bg-emerald-600 text-white rounded hover:bg-emerald-700"
                    >
                      {isBulk ? `Add to Campaign · ${candidateIds.length}` : 'Add to Campaign'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {step === 'customize' && pickedCampaign && (
        <CampaignCustomizerModal
          inline
          campaignId={pickedCampaign.id}
          campaignName={pickedCampaign.name}
          candidateId={candidateIds[0]}
          onCancel={onClose}
          onBack={handleCustomizerBack}
          onContinue={handleCustomizerContinue}
        />
      )}

      {step === 'contacts' && pickedCampaign && (
        <ContactPickerModal
          inline
          candidateIds={candidateIds}
          campaignId={pickedCampaign.id}
          campaignName={pickedCampaign.name}
          pipelineId={pipelineId || null}
          stepOverrides={stepOverrides}
          onClose={onClose}
          onBack={handleContactPickerBack}
          onEnrolled={onEnrolled}
        />
      )}
    </div>
  );
};

export default AddToCampaignSidebar;
